import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  FiPlay,
  FiArrowLeft,
  FiCheck,
  FiUser,
  FiMessageSquare,
  FiSettings,
  FiZap,
  FiClock,
  FiStar,
  FiKey
} from 'react-icons/fi';

const QuickStartGuide = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const steps = [
    {
      step: 1,
      title: 'Sign Up for Bervin',
      description: 'Create your account and get instant access to multiple AI models.',
      icon: FiUser,
      details: [
        'Visit bervin.appiworks.com/app',
        'Click "Sign Up" in the top right corner',
        'Enter your email and create a password',
        'Verify your email address',
        'Complete your profile setup'
      ],
      time: '2 minutes'
    },
    {
      step: 2,
      title: 'Setup Your Provider API Keys',
      description: 'Configure your AI provider API keys to access different AI models and services.',
      icon: FiKey,
      details: [
        'Navigate to Settings > API Keys from your dashboard',
        'Add your OpenAI API key for GPT models',
        'Configure Anthropic API key for Claude models',
        'Set up Google AI API key for Gemini models',
        'Test your API connections to ensure they work'
      ],
      time: '5 minutes'
    },
    {
      step: 3,
      title: 'Start Your First Conversation',
      description: 'Begin chatting with AI models and experience the power of multi-AI conversations.',
      icon: FiMessageSquare,
      details: [
        'Click "New Conversation" from your dashboard',
        'Choose your preferred AI model (or select multiple)',
        'Type your first question or prompt',
        'Watch as different AI models provide their perspectives',
        'Compare responses to get comprehensive insights'
      ],
      time: '3 minutes'
    },
    {
      step: 4,
      title: 'Customize Your Experience',
      description: 'Set up your preferences and explore advanced features.',
      icon: FiSettings,
      details: [
        'Access Settings from the sidebar menu',
        'Set your default AI models',
        'Configure notification preferences',
        'Explore conversation templates',
        'Set up team collaboration if needed'
      ],
      time: '5 minutes'
    },
    {
      step: 5,
      title: 'Explore Advanced Features',
      description: 'Discover powerful tools to enhance your AI interactions.',
      icon: FiZap,
      details: [
        'Try multi-AI comparison mode',
        'Use conversation branching',
        'Save and organize your conversations',
        'Share insights with your team',
        'Explore custom prompts and templates'
      ],
      time: '10 minutes'
    }
  ];

  const tips = [
    {
      title: 'Secure Your API Keys',
      description: 'Keep your API keys safe and never share them publicly. Store them securely in Bervin\'s encrypted settings.'
    },
    {
      title: 'Pro Tip: Ask Follow-up Questions',
      description: 'Get deeper insights by asking follow-up questions based on AI responses.'
    },
    {
      title: 'Use Multiple Models',
      description: 'Different AI models excel at different tasks. Use multiple models for comprehensive perspectives.'
    },
    {
      title: 'Save Important Conversations',
      description: 'Bookmark valuable conversations for future reference and team sharing.'
    },
    {
      title: 'Experiment with Prompts',
      description: 'Try different ways of asking the same question to get varied and richer responses.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-900 via-dark-800 to-secondary-900 text-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Breadcrumb */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center space-x-3 mb-8"
          >
            <Link 
              to="/guide" 
              className="text-gray-400 hover:text-white transition-colors duration-200 flex items-center"
            >
              <FiArrowLeft className="w-4 h-4 mr-2" />
              Guide
            </Link>
            <span className="text-gray-500">/</span>
            <span className="text-secondary-400">Quick Start Guide</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-full mb-8"
            >
              <FiPlay className="w-10 h-10 text-white" />
            </motion.div>
            
            <h1 className="text-5xl md:text-6xl font-display font-bold mb-6">
              <span className="gradient-text">Quick Start Guide</span>
            </h1>
            
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Get up and running with Bervin in minutes. This guide will walk you through 
              everything you need to know to start having productive conversations with AI.
            </p>

            <div className="flex items-center justify-center space-x-6 mt-8">
              <div className="flex items-center space-x-2 text-gray-400">
                <FiClock className="w-5 h-5" />
                <span>25 min total</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-400">
                <FiStar className="w-5 h-5" />
                <span>Beginner Friendly</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Step-by-Step Guide */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-6 gradient-text">
              Get Started in 5 Easy Steps
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Follow these steps to master the basics of Bervin
            </p>
          </motion.div>

          <div className="space-y-16">
            {steps.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 * index }}
                className={`flex flex-col ${index % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-12`}
              >
                <div className="flex-1">
                  <div className="flex items-center mb-6">
                    <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-full text-xl font-bold text-white mr-4">
                      {step.step}
                    </div>
                    <div>
                      <h3 className="text-3xl font-display font-bold text-white mb-2">
                        {step.title}
                      </h3>
                      <div className="flex items-center space-x-2 text-gray-400">
                        <FiClock className="w-4 h-4" />
                        <span>{step.time}</span>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                    {step.description}
                  </p>
                  
                  <ul className="space-y-3">
                    {step.details.map((detail, detailIndex) => (
                      <li key={detailIndex} className="flex items-start text-gray-300">
                        <FiCheck className="w-5 h-5 text-green-400 mr-3 flex-shrink-0 mt-0.5" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="flex-1 flex justify-center">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-secondary-500/20 to-accent-500/20 rounded-3xl blur-xl"></div>
                    <div className="relative w-80 h-64 bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-3xl flex items-center justify-center">
                      <step.icon className="w-24 h-24 text-secondary-400" />
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tips Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-6 gradient-text">
              Pro Tips for Success
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Make the most of your Bervin experience with these expert tips
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {tips.map((tip, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 * index }}
                className="group relative"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-secondary-500/10 to-accent-500/10 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:border-secondary-500/50 transition-all duration-300">
                  <div className="flex items-center mb-4">
                    <div className="w-3 h-3 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-full mr-3"></div>
                    <h3 className="text-xl font-display font-bold text-white">
                      {tip.title}
                    </h3>
                  </div>
                  
                  <p className="text-gray-300 leading-relaxed">
                    {tip.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="bg-gradient-to-r from-dark-800/50 to-secondary-900/50 backdrop-blur-sm border border-white/10 rounded-3xl p-12"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-6 gradient-text">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              You're all set! Jump into Bervin and start exploring the power of AI collaboration.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <a
                href="https://bervin.appiworks.com/app"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-xl font-semibold text-white text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
              >
                Launch Bervin Now
              </a>
              <Link
                to="/guide"
                className="px-8 py-4 border border-white/20 rounded-xl font-semibold text-white text-lg hover:bg-white/10 transition-all duration-300"
              >
                Back to Guides
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default QuickStartGuide;
